import { ApolloClient } from '@apollo/client'
import { useUnsafeCallback } from '@factorialco/deprecated-design-system'
import {
  AvatarNameSelectorDocument,
  CostCentersEmployeesStructuredAssignmentsDocument,
  CostCentersEmployeesStructuredAssignmentsQuery,
  CostCentersListDocument,
  EmployeesTeamsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'
import { useWorkplaceFilter } from 'modules/employees/lib/filters/workplace'
import { useJobCatalogRoleFilter } from 'modules/job_catalog/lib/filters/jobRole'

import {
  ACTIVE,
  TERMINATED,
} from 'modules/cost_centers/components/CostCentersPeopleAssignment/constants'
import { FormattedCostCenterEmployeeWithStructuredAssignment } from 'modules/cost_centers/components/CostCentersPeopleAssignment/types'
import {
  getStructuredCostCenterName,
  mapChildrenData,
  mapEmployeeData,
  mapVariables,
} from 'modules/cost_centers/components/CostCentersPeopleAssignment/utils'

interface UsePeopleAssignmentDataSourceProps {
  client: ApolloClient<object>
  onItemClick: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => void
}

export const usePeopleAssignmentDataSource = ({
  client,
  onItemClick,
}: UsePeopleAssignmentDataSourceProps) => {
  const { company } = useCurrentSession()
  const workplaceFilter = useWorkplaceFilter()

  // Structured CC assignments only use new tree-based job catalog
  const jobCatalogRoleFilter = useJobCatalogRoleFilter({ hasJobCatalogTreeFeature: true })

  const handleMapData = useUnsafeCallback(
    (data: CostCentersEmployeesStructuredAssignmentsQuery) => mapEmployeeData(data),
    []
  )

  const handleMapChildren = useUnsafeCallback(
    (item: FormattedCostCenterEmployeeWithStructuredAssignment) => mapChildrenData(item),
    []
  )

  const activeByDefault = { peopleStatus: [ACTIVE] }

  const source = useGraphqlDataCollectionSource({
    document: CostCentersEmployeesStructuredAssignmentsDocument,
    fetchPolicy: 'network-only',
    paginationType: 'infinite-scroll' as const,
    perPage: 20,
    mapData: (tableData: CostCentersEmployeesStructuredAssignmentsQuery) =>
      handleMapData(tableData),
    sortings: {
      fullName: {
        label: i18n.t('finance.account_list.headers.name'),
        sortBy: 'fullName',
      },
    },
    defaultSortings: {
      field: 'fullName' as const,
      order: 'asc' as const,
    },
    search: {
      enabled: true,
    },
    defaultFilters: activeByDefault,
    currentFilters: activeByDefault,
    filters: {
      legalEntityIds: {
        type: 'in' as const,
        label: i18n.t('finance.cost_centers_list.filters.legal_entity'),
        options: {
          options: company.legalEntities.map(({ id, legalName }) => ({
            value: id,
            label: legalName || '',
          })),
        },
      },
      costCenterIds: {
        type: 'in' as const,
        label: i18n.t('finance.cost_centers_list.filters.cost_center.label'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: CostCentersListDocument,
              fetchPolicy: 'cache-first',
              variables: {
                sourceType: 'structure_based',
              },
            })

            return data.costCenters.costCentersConnection.edges.map((costCenter) => ({
              label: getStructuredCostCenterName(costCenter.node),
              value: costCenter.node.id,
            }))
          },
        },
      },
      employeeIds: {
        type: 'in' as const,
        label: i18n.t('finance.settings.cost_centers.people'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: AvatarNameSelectorDocument,
              variables: {
                companyId: company.id,
                includeTeams: false,
                includeWorkplaces: false,
              },
              fetchPolicy: 'cache-first',
            })

            return data.employees.employeeNames.map((employee) => ({
              label: employee.fullName,
              value: employee.id,
            }))
          },
        },
      },
      peopleStatus: {
        type: 'in' as const,
        label: i18n.t('finance.cost_center_people_list.people_status'),
        options: {
          options: [
            { label: i18n.t('employees.employees_list.status.active'), value: ACTIVE },
            { label: i18n.t('employees.employees_list.status.terminated'), value: TERMINATED },
          ],
        },
      },
      teamIds: {
        type: 'in' as const,
        label: i18n.t('filters.filter.team'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: EmployeesTeamsDocument,
              fetchPolicy: 'cache-first',
            })

            return data.teams.teams.map((team) => ({
              label: team.name,
              value: team.id,
            }))
          },
        },
      },
      locationIds: {
        ...workplaceFilter,
        label: i18n.t('finance.cost_centers_list.filters.workplace'),
      },
      jobRole: jobCatalogRoleFilter,
    },
    presetsLoading: false,
    mapVariables,
    mapPagination: (tableData: CostCentersEmployeesStructuredAssignmentsQuery) => ({
      total: tableData.employees.employeesConnection.totalCount ?? 0,
      cursor: tableData.employees.employeesConnection.pageInfo.endCursor ?? '0',
      hasNextPage: tableData.employees.employeesConnection.pageInfo.hasNextPage ?? false,
    }),
    totalItemSummary: (totalCount: number) =>
      i18n.tp('finance.cost_center_people_list.count_employee_assignments', {
        count: totalCount,
      }),
    itemOnClick: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => () => {
      onItemClick(item)
    },
    itemsWithChildren: (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
      return !item.isChild && item.structuredAssignments.edges.length > 1
    },
    fetchChildren: ({ item }: { item: FormattedCostCenterEmployeeWithStructuredAssignment }) => {
      return handleMapChildren(item)
    },
  })

  return source
}
